import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BaseAPI } from 'src/providers/base-api';

// ============================================================================
// Types
// ============================================================================

export interface OrgConfig {
    dateFormat: string;               // e.g., 'dd-MM-yyyy', 'yyyy-MM-dd'
    employeeIdPattern: string;        // e.g., 'CR-EMP-'
    employeeIdPatternDigit: string;   // e.g., '4'
    offerLetterIdPattern: string;
    offerLetterIdPatternDigit: string;
    // TODO: Backend - Add these fields to OtherSettings in database and forms
    // TODO: Frontend - Add UI controls in datepage.tsx for these settings
    timezone?: string;                // e.g., 'Asia/Kolkata'
    currencyCode?: string;            // e.g., 'INR', 'USD'
}

export interface OrgProfile {
    companyName: string;
    companyEmail: string;
    website: string;
    companyLogo: string;
    logoAssetId: string;
    contactNumber: string;
    panNumber: string;
    gstNumber: string;
    cinNumber: string;
    companyType: string;
}

interface SettingsState {
    orgConfig: OrgConfig | null;
    orgProfile: OrgProfile | null;
    loading: boolean;
    loaded: boolean;
    error: string | null;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: SettingsState = {
    orgConfig: null,
    orgProfile: null,
    loading: false,
    loaded: false,
    error: null,
};

// Default config when API fails or returns empty
const defaultOrgConfig: OrgConfig = {
    dateFormat: 'yyyy-MM-dd',
    employeeIdPattern: '',
    employeeIdPatternDigit: '',
    offerLetterIdPattern: '',
    offerLetterIdPatternDigit: '',
    timezone: 'Asia/Kolkata',
    currencyCode: 'INR',
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch org settings from backend.
 * This should be called once on app init (after auth).
 */
export const fetchOrgSettings = createAsyncThunk<
    { orgConfig: OrgConfig; orgProfile: OrgProfile | null },
    void,
    { rejectValue: string }
>('settings/fetchOrgSettings', async (_, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
        BaseAPI.getSettingsDetails((result: any) => {
            if (result.status === 200 && Array.isArray(result.data)) {
                let orgConfig: OrgConfig = { ...defaultOrgConfig };
                let orgProfile: OrgProfile | null = null;

                result.data.forEach((item: any) => {
                    if (item.group === 'OtherSettings' && item.value) {
                        orgConfig = {
                            ...defaultOrgConfig,
                            ...item.value,
                        };
                    }
                    if (item.group === 'OrganisationProfile' && item.value) {
                        orgProfile = item.value;
                    }
                });

                resolve({ orgConfig, orgProfile });
            } else {
                reject(rejectWithValue('Failed to fetch settings'));
            }
        });
    });
});

// ============================================================================
// Slice
// ============================================================================

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        /**
         * Clear settings (useful on logout)
         */
        clearSettings: (state) => {
            state.orgConfig = null;
            state.orgProfile = null;
            state.loaded = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrgSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchOrgSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.loaded = true;
                state.orgConfig = action.payload.orgConfig;
                state.orgProfile = action.payload.orgProfile;
            })
            .addCase(fetchOrgSettings.rejected, (state, action) => {
                state.loading = false;
                state.loaded = true;
                state.error = action.payload || 'Failed to load settings';
                // Use defaults on error
                state.orgConfig = defaultOrgConfig;
            });
    },
});

// ============================================================================
// Exports
// ============================================================================

export const { clearSettings } = settingsSlice.actions;

export default settingsSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

export const selectOrgConfig = (state: { settings: SettingsState }) =>
    state.settings.orgConfig;

export const selectOrgProfile = (state: { settings: SettingsState }) =>
    state.settings.orgProfile;

export const selectSettingsLoading = (state: { settings: SettingsState }) =>
    state.settings.loading;

export const selectSettingsLoaded = (state: { settings: SettingsState }) =>
    state.settings.loaded;

export const selectDateFormat = (state: { settings: SettingsState }) =>
    state.settings.orgConfig?.dateFormat || 'yyyy-MM-dd';
